import {useMemo, useState} from "react";
import {makeStyles, TextField} from "@material-ui/core";
import Tr from "translations/Translations";
import clsx from "clsx";
import PropTypes from "prop-types";
import GridBlock from "components/GridBlock";
import {KeyboardDatePicker} from "@material-ui/pickers";
import DateFormats from "constants/DateFormats";
import moment from "libs/moment";
import Button from "components/Button";
import BoldTypography from "components/BoldTypography";
import WorkableButton from "components/button/WorkableButton";
import SimpleSelect from "components/SimpleSelect";
import {createSelectItem} from "components/Select";
import {connect} from "react-redux";
import OfferChoiceService from "services/OfferChoiceService";
import Confirm from "components/popup/confirm/Confirm";
import {RoundType} from "constants/RoundType";
import AuctionHistoryPopup from "./auction/AuctionHistoryPopup";
import {OfferChoiceState} from "../constants/OfferChoiceState";

const useStyles = makeStyles(theme => ({
    choiceSize: {
        "& .MuiInput-root": {
            fontSize: theme.typography.body2.fontSize,
            color: theme.palette.text.primary
        }
    },
    choiceStatus: {
        marginBottom: theme.spacing()
    },
    choiceStatusNeedsApproval: {
        color: theme.palette.common.error
    },
    choiceControls: {
        marginTop: theme.spacing(2),
        textAlign: 'center'
    },
    choiceControlsEnd: {
        textAlign: 'end'
    },
    offerChoiceJustification: {
        width: 300,
        marginTop: theme.spacing(2)
    },
    menuItemClassName: {
        whiteSpace: 'normal'
    },
    auctionHistory: {
        float: 'right',
    }
}));

function OfferChoiceSummaryCell(
    {
        demandId,
        editable,
        rowWorking,
        working,
        offerChoiceRow,
        chosenStatusDone,
        setChosenStatusDone,
        chosenSuppliers,
        updateStatus,
        modifyChoice,
        offerChoiceJustifications
    }) {
    const classes = useStyles();
    const [auctionHistoryPopup, setAuctionHistoryPopup] = useState(false);
    const [offerChoiceDateError, setOfferChoiceDateError] = useState(false);
    const [modifyConfirmOpen, setModifyConfirmOpen] = useState(false);
    const [offerChoiceJustification, setOfferChoiceJustification] = useState(offerChoiceRow.offerChoiceJustification || '');
    const [offerChoiceDate, setOfferChoiceDate] = useState(offerChoiceRow.offerChoiceDate ? moment(offerChoiceRow.offerChoiceDate) : null);

    const handleChangeDate = (date) => {
        const dateValue = date ? date : null;
        setOfferChoiceDateError(!dateValue);
        setOfferChoiceDate(dateValue)
    }

    const onUpdateStatus = () => {

        if (!offerChoiceDate || !moment.isMoment(offerChoiceDate) || !offerChoiceDate.isValid()) {
            setOfferChoiceDateError(true);
            return;
        }

        const canceled = chosenSuppliers.length === 0;
        updateStatus(offerChoiceRow.identity, canceled, canceled ? null : chosenSuppliers, offerChoiceDate, offerChoiceJustification).then(() => {
            setChosenStatusDone(true);
        }).catch((e) => console.error(e));

    };

    const disabledInputs = working || chosenStatusDone || !editable;

    const auctionHistoryVisible = useMemo(() => offerChoiceRow.currentAdditionalRoundInfo
        && offerChoiceRow.currentAdditionalRoundInfo.type === RoundType.AUCTION, [offerChoiceRow.currentAdditionalRoundInfo]);

    const resolveRowStatus = (offerChoiceRow) => {
        if (OfferChoiceState.REJECTED === offerChoiceRow.state)
            return 'offer_choice_rejected';

        let status = offerChoiceRow.canceled ? 'offer_choice_cancelled' : 'offer_choice_chosen';
        if (chosenStatusDone && OfferChoiceState.ACCEPTED !== offerChoiceRow.state)
            status += ('_' + offerChoiceRow.state.toLowerCase());
        return status;
    }

    return (
        <GridBlock column>
            {auctionHistoryVisible &&
            <Button className={classes.auctionHistory} onClick={() => setAuctionHistoryPopup(true)} variant="outlined">{Tr('auctions_information_button')}</Button>
            }

            {(chosenStatusDone || OfferChoiceState.REJECTED === offerChoiceRow.state) &&
            <BoldTypography className={clsx(classes.choiceStatus, {[classes.choiceStatusNeedsApproval]: OfferChoiceState.READY === offerChoiceRow.state})}>
                {Tr(resolveRowStatus(offerChoiceRow))}
            </BoldTypography>}

            <KeyboardDatePicker
                disabled={disabledInputs}
                className={classes.choiceSize}
                label={Tr('choice_date')}
                format={DateFormats.DATE_FORMAT}
                variant='dialog'
                cancelLabel={Tr('cancel')}
                value={offerChoiceDate}
                invalidDateMessage={Tr("invalid_date_format")}
                onChange={handleChangeDate}
                error={offerChoiceDateError}
                helperText={offerChoiceDateError ? Tr('required_field') : undefined}
            />
            {!chosenStatusDone && chosenSuppliers.length === 0 &&
            <SimpleSelect
                className={classes.offerChoiceJustification}
                menuItemClassName={classes.menuItemClassName}
                items={[createSelectItem("empty", Tr('select_justifiction')), ...Object.keys(offerChoiceJustifications).map(x => createSelectItem(x, offerChoiceJustifications[x]))]}
                onChange={(event) => setOfferChoiceJustification(event.target.value === "empty" ? "" : offerChoiceJustifications[event.target.value])}
                value={"empty"}
            />}

            <TextField
                disabled={disabledInputs}
                className={classes.choiceSize}
                label={Tr('choice_justification')}
                multiline
                fullWidth
                maxRows={5}
                value={offerChoiceJustification}
                onChange={(e => setOfferChoiceJustification(e.target.value))}
            />

            {
                editable &&
                <div className={clsx(classes.choiceControls, {[classes.choiceControlsEnd]: chosenStatusDone})}>
                    {chosenStatusDone ? (OfferChoiceState.PENDING !== offerChoiceRow.state &&
                        <WorkableButton working={rowWorking} disabled={rowWorking || working || !editable} onClick={() => setModifyConfirmOpen(true)}>
                            {Tr('choose_modify')}
                        </WorkableButton>) :
                        <WorkableButton working={rowWorking} disabled={rowWorking || working || !editable}
                                        onClick={onUpdateStatus}>{chosenSuppliers.length > 0 ? Tr('confirm') : Tr('invalidate')}</WorkableButton>
                    }
                </div>
            }

            {auctionHistoryPopup && <AuctionHistoryPopup
                onClose={()=> setAuctionHistoryPopup(false)}
                positionId={offerChoiceRow.identity}
                demandId={demandId}
                />}

            <Confirm
                confirmText={Tr('choose_modify_confirm', {order: offerChoiceRow.order})}
                onCancel={() => setModifyConfirmOpen(false)}
                onConfirm={() => {
                    setModifyConfirmOpen(false);
                    modifyChoice(offerChoiceRow.identity);
                }}
                open={modifyConfirmOpen}
                translateConfirmTexts={false}
            />
        </GridBlock>)
}

OfferChoiceSummaryCell.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool.isRequired,
    working: PropTypes.bool.isRequired,
    rowWorking: PropTypes.bool.isRequired,
    offerChoiceRow: PropTypes.object.isRequired,
    chosenStatusDone: PropTypes.bool.isRequired,
    setChosenStatusDone: PropTypes.func.isRequired,
    chosenSuppliers: PropTypes.array.isRequired,
    updateStatus: PropTypes.func.isRequired,
    modifyChoice: PropTypes.func.isRequired,
}

export default connect((state) => ({
    offerChoiceJustifications: state.resources[OfferChoiceService.OFFER_CHOICE_JUSTIFICATIONS]
}), {})(OfferChoiceSummaryCell);
;

